import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Modal, Inp } from "./Enquiries";
import { Plus, Trash, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

import { API } from "../../lib/api";

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    const [i, p] = await Promise.all([api.get("/invoices"), api.get("/patients")]);
    setItems(i.data);
    setPatients(p.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoicing & Payments" subtitle={`${items.length} invoice${items.length===1?"":"s"}`}
        actions={
          <button onClick={()=>setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
            data-testid="add-invoice-btn">
            <Plus size={16} weight="bold"/> New invoice
          </button>
        }/>
      <div className="px-6 sm:px-10 py-8">
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <Th>Invoice</Th><Th>Patient</Th><Th>Total</Th><Th>Paid</Th><Th>Balance</Th><Th>Status</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td>
                    <div className="font-bold">INV-{i.id.slice(0,6).toUpperCase()}</div>
                    <div className="text-xs text-zinc-500">{i.created_at?.slice(0,10)}</div>
                  </Td>
                  <Td>{i.patient?.name || "—"}</Td>
                  <Td>₹{i.total.toFixed(0)}</Td>
                  <Td>₹{i.paid.toFixed(0)}</Td>
                  <Td>₹{i.balance.toFixed(0)}</Td>
                  <Td>
                    <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-sm ${
                      i.status === "paid" ? "bg-green-100 text-green-700"
                      : i.status === "partial" ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"}`}>{i.status}</span>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-3">
                      <a href={`${API}/invoices/${i.id}/pdf`} target="_blank" rel="noopener noreferrer"
                         className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 hover:text-[#E60000] inline-flex items-center gap-1"
                         data-testid={`pdf-${i.id}`}>
                        <FilePdf size={14} weight="bold"/> PDF
                      </a>
                      {i.balance > 0 && (
                        <button onClick={()=>setPayFor(i)} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000]" data-testid={`pay-${i.id}`}>Take payment ›</button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-12 text-sm">No invoices yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewInvoice patients={patients} onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false); load();}}/>}
      {payFor && <PayModal invoice={payFor} onClose={()=>setPayFor(null)} onSaved={()=>{setPayFor(null); load();}}/>}
    </div>
  );
}

function Th({ children }) { return <th className="text-left text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-700 px-4 py-3">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3">{children}</td>; }

function NewInvoice({ patients, onClose, onSaved }) {
  const [patientId, setPatientId] = useState(patients[0]?.id || "");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [busy, setBusy] = useState(false);

  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const afterDisc = Math.max(0, subtotal - Number(discount || 0));
  const taxAmount = afterDisc * (Number(tax || 0) / 100);
  const total = afterDisc + taxAmount;

  const save = async () => {
    if (!patientId) { toast.error("Select a patient"); return; }
    setBusy(true);
    try {
      await api.post("/invoices", {
        patient_id: patientId,
        items: items.map(i => ({ description: i.description, quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 })),
        discount: Number(discount) || 0,
        tax: Number(tax) || 0,
      });
      toast.success("Invoice created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const updItem = (idx, patch) => setItems((s) => s.map((it, i) => i === idx ? { ...it, ...patch } : it));

  return (
    <Modal onClose={onClose} title="New invoice">
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Patient</label>
          <select value={patientId} onChange={(e)=>setPatientId(e.target.value)}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900" data-testid="inv-patient">
            <option value="">— select —</option>
            {patients.map(p => <option key={p.id} value={p.id}>{p.name} ({p.phone})</option>)}
          </select>
        </div>

        <div className="border border-zinc-200 rounded-sm">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-700">
            <div className="col-span-6">Item</div>
            <div className="col-span-2">Qty</div>
            <div className="col-span-3">Unit ₹</div>
            <div className="col-span-1"></div>
          </div>
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-zinc-100">
              <input value={it.description} onChange={(e)=>updItem(idx, { description: e.target.value })}
                className="col-span-6 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                placeholder="Treatment / Service" data-testid={`inv-item-desc-${idx}`}/>
              <input type="number" value={it.quantity} onChange={(e)=>updItem(idx, { quantity: e.target.value })}
                className="col-span-2 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                data-testid={`inv-item-qty-${idx}`}/>
              <input type="number" value={it.unit_price} onChange={(e)=>updItem(idx, { unit_price: e.target.value })}
                className="col-span-3 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                data-testid={`inv-item-price-${idx}`}/>
              <button onClick={()=>setItems((s)=>s.filter((_,i)=>i!==idx))} className="col-span-1 text-zinc-400 hover:text-[#E60000] flex items-center justify-center">
                <Trash size={14} weight="bold"/>
              </button>
            </div>
          ))}
          <button onClick={()=>setItems([...items, { description: "", quantity: 1, unit_price: 0 }])}
            className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] px-3 py-2" data-testid="inv-add-item">
            + Add line
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Inp label="Discount ₹" type="number" v={discount} onChange={setDiscount} testid="inv-discount"/>
          <Inp label="Tax %" type="number" v={tax} onChange={setTax} testid="inv-tax"/>
        </div>

        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-1 text-sm">
          <Row k="Subtotal" v={`₹${subtotal.toFixed(2)}`} />
          <Row k="Discount" v={`-₹${Number(discount||0).toFixed(2)}`} />
          <Row k="Tax" v={`+₹${taxAmount.toFixed(2)}`} />
          <Row k="Total" v={`₹${total.toFixed(2)}`} big />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">Cancel</button>
        <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="inv-save">
          {busy ? "Saving…" : "Create invoice"}
        </button>
      </div>
    </Modal>
  );
}

function Row({ k, v, big }) {
  return (
    <div className={`flex justify-between ${big ? "border-t border-zinc-300 pt-2 mt-2 font-bold text-base" : ""}`}>
      <span className="text-zinc-700">{k}</span>
      <span>{v}</span>
    </div>
  );
}

function PayModal({ invoice, onClose, onSaved }) {
  const [amount, setAmount] = useState(invoice.balance);
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/pay`, { amount: Number(amount), method });
      toast.success("Payment recorded");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Take payment · INV-${invoice.id.slice(0,6).toUpperCase()}`}>
      <div className="text-sm text-zinc-600 mb-4">Outstanding balance: <span className="font-bold text-zinc-900">₹{invoice.balance.toFixed(0)}</span></div>
      <div className="grid grid-cols-2 gap-3">
        <Inp label="Amount ₹" type="number" v={amount} onChange={setAmount} testid="pay-amount"/>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Method</label>
          <select value={method} onChange={(e)=>setMethod(e.target.value)}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900" data-testid="pay-method">
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="bank">Bank transfer</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">Cancel</button>
        <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="pay-save">
          {busy ? "Saving…" : "Record payment"}
        </button>
      </div>
    </Modal>
  );
}
